import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Input,
	Label,
	Badge,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Textarea,
} from "@/components/primitives";
import { Plus, Edit, Target } from "lucide-react";

interface Topic {
	id: string;
	title: string;
	description: string;
	initial_question: string;
	created_at: string;
	assigned_groups?: { id: string; name: string }[];
}

export function TopicManagement() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [topics, setTopics] = useState<Topic[]>([]);
	const [loading, setLoading] = useState(true);

	// Create topic state
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	const [newTopicTitle, setNewTopicTitle] = useState("");
	const [newTopicDescription, setNewTopicDescription] = useState("");
	const [newTopicQuestion, setNewTopicQuestion] = useState("");
	const [createStep, setCreateStep] = useState<number>(1);

	// Edit topic state
	const [showEditDialog, setShowEditDialog] = useState(false);
	const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
	const [editTopicTitle, setEditTopicTitle] = useState("");
	const [editTopicDescription, setEditTopicDescription] = useState("");
	const [editTopicInitialQuestion, setEditTopicInitialQuestion] = useState("");

	useEffect(() => {
		if (user?.id) fetchData();
	}, [user?.id]);

	useEffect(() => {
		if (showCreateDialog) {
			setCreateStep(1);
			// keep existing field values if want to preserve, otherwise reset
		}
	}, [showCreateDialog]);

	const fetchData = async () => {
		try {
			await fetchTopics();
		} catch (error) {
			console.error("Error in fetchData:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchTopics = async () => {
		try {
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();
			if (sessionError || !session?.access_token) {
				setTopics([]);
				return;
			}
			const { data, error } = await supabase.functions.invoke(
				"fetch-mentor-topics",
				{
					headers: { Authorization: `Bearer ${session.access_token}` },
				},
			);
			if (error) {
				console.error("Edge function fetch-mentor-topics error:", error);
				setTopics([]);
				return;
			}
			const apiTopics = (data?.topics || []) as Array<any>;
			setTopics(apiTopics);
		} catch (e) {
			console.error("Error fetching topics via function:", e);
			setTopics([]);
		}
	};

	const createTopic = async () => {
		if (!newTopicTitle.trim()) return;

		// Try to insert including new columns; if they don't exist, retry without them
		let res = await supabase.from("topics").insert({
			created_by: user?.id,
			name: newTopicTitle,
			description: newTopicDescription,
			initial_question: newTopicQuestion,
			prompt_creation: newTopicQuestion,
		});

		if (
			res.error &&
			/initial_question|prompt_creation/.test(res.error.message || "")
		) {
			// Retry without the new columns for backwards compatibility
			const retry = await supabase.from("topics").insert({
				created_by: user?.id,
				name: newTopicTitle,
				description: newTopicDescription,
			});
			if (retry.error) {
				alert("Error creating topic: " + retry.error.message);
				return;
			}
		} else if (res.error) {
			alert("Error creating topic: " + res.error.message);
			return;
		}

		// Success
		setNewTopicTitle("");
		setNewTopicDescription("");
		setNewTopicQuestion("");
		setCreateStep(1);
		setShowCreateDialog(false);
		fetchTopics();
	};

	const deleteTopic = async (topicId: string) => {
		if (
			!confirm(
				"Are you sure you want to delete this topic? This action cannot be undone.",
			)
		)
			return;

		// Ensure only the creator can delete the topic
		const { error } = await supabase
			.from("topics")
			.delete()
			.eq("id", topicId)
			.eq("created_by", user?.id);

		if (error) {
			alert("Error deleting topic: " + error.message);
		} else {
			fetchData();
		}
	};

	const updateTopic = async () => {
		if (!editingTopic || !editTopicTitle.trim()) return;

		// Try to update including new columns; if schema doesn't have them, retry without
		let res = await supabase
			.from("topics")
			.update({
				name: editTopicTitle,
				description: editTopicDescription,
				initial_question: editTopicInitialQuestion,
				prompt_creation: editTopicInitialQuestion,
			})
			.eq("id", editingTopic.id)
			.eq("created_by", user?.id);

		if (
			res.error &&
			/initial_question|prompt_creation/.test(res.error.message || "")
		) {
			const retry = await supabase
				.from("topics")
				.update({
					name: editTopicTitle,
					description: editTopicDescription,
				})
				.eq("id", editingTopic.id)
				.eq("created_by", user?.id);

			if (retry.error) {
				alert("Error updating topic: " + retry.error.message);
				return;
			}
		} else if (res.error) {
			alert("Error updating topic: " + res.error.message);
			return;
		}

		setEditingTopic(null);
		setEditTopicTitle("");
		setEditTopicDescription("");
		setEditTopicInitialQuestion("");
		setShowEditDialog(false);
		fetchTopics();
	};

	if (loading) {
		return (
			<div className="text-center py-8">
				<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
				<p className="text-muted-foreground">Loading topic management...</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header and Actions */}
			<div className="flex justify-between items-start">
				<div>
					<h2 className="text-2xl font-bold">Topic Management</h2>
					<p className="text-muted-foreground">
						Create and manage reflection topics for your mentees
					</p>
				</div>
				<div>
					<Button onClick={() => navigate("/mentor/topic/new")}>
						<Plus className="w-4 h-4 mr-2" />
						Create Topic
					</Button>
				</div>
			</div>

			{/* Topics List */}
			<Card>
				<CardHeader>
					<CardTitle>Your Topics</CardTitle>
					<CardDescription>Manage your reflection topics</CardDescription>
				</CardHeader>
				<CardContent>
					{topics.length === 0 ? (
						<div className="text-center py-8 text-muted-foreground">
							<Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
							<p>
								No topics created yet. Create your first topic to get started.
							</p>
						</div>
					) : (
						<div className="space-y-4">
							{topics.map((topic) => (
								<div key={topic.id} className="border rounded-lg p-4">
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<h4 className="font-medium">{topic.title}</h4>
											{topic.description && (
												<p className="text-sm text-muted-foreground mt-1">
													{topic.description}
												</p>
											)}

											{topic.assigned_groups &&
												topic.assigned_groups.length > 0 && (
													<div className="mt-2">
														<p className="text-xs font-medium text-muted-foreground mb-1">
															Groups:
														</p>
														<div className="flex flex-wrap gap-1">
															{topic.assigned_groups.map((group) => (
																<Badge
																	key={group.id}
																	variant="outline"
																	className="text-xs"
																>
																	{group.name}
																</Badge>
															))}
														</div>
													</div>
												)}
											<p className="text-xs text-muted-foreground mt-2">
												Created:{" "}
												{new Date(topic.created_at).toLocaleDateString()}
											</p>
										</div>
										<div className="flex items-center space-x-2">
											<Button
												size="sm"
												variant="ghost"
												onClick={() =>
													navigate(`/mentor/topic/${topic.id}/edit`)
												}
											>
												<Edit className="w-4 h-4 mr-1" />
												Edit
											</Button>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
